import React, { useEffect, useState } from "react";

import { ActionBar } from "../shared/ActionBar";
import { Action, Alert, Card, EmptyState, Skeleton } from "../shared/StudioControls";
import { AudienceStep } from "./audienceStep/AudienceStep";
import { AnalyticsStep } from "./analyticsStep/AnalyticsStep";
import {
  AUTOMATED_CAMPAIGN_STEPS,
  CampaignStep,
  getStepInstruction
} from "./campaignDetails.utils";
import { ContentStep } from "./contentStep/ContentStep";
import { CampaignContextHeader } from "./contextHeader/CampaignContextHeader";
import { TestStep } from "./testStep/TestStep";

const findCycleStart = (workspace, campaignId) => {
  const cycle = workspace.cycles.data.find(item => (item.campaigns || []).some(campaign => campaign.id === campaignId));
  return cycle ? cycle.start_date : null;
};

export const CampaignDetails = ({ model, derived, currentStep, action, handlers }) => {
  const [activeTab, setActiveTab] = useState(currentStep || CampaignStep.Audience);
  useEffect(() => {
    if (currentStep === CampaignStep.Audience || currentStep === CampaignStep.Content) setActiveTab(currentStep);
  }, [currentStep]);
  if (!derived) {
    return (
      <div className="cs-page">
        <button type="button" className="cs-back" onClick={handlers.onBack}>
          <span aria-hidden="true">‹</span>
          <span>Notifications</span>
        </button>
        {model.loading && <Skeleton heights={[120, 140, 320]} label="Loading notification set" />}
        {!model.loading && (
          <Card>
            <EmptyState title="We couldn’t load this notification set" description={model.readError || model.error || "Check your connection and try again."}>
              {!model.forbidden && <Action variant="primary" onClick={handlers.onRefresh}>Try again</Action>}
            </EmptyState>
          </Card>
        )}
      </div>
    );
  }

  const { campaign } = derived;
  if (!campaign.payload.automation) {
    return (
      <div className="cs-page">
        <button type="button" className="cs-back" onClick={handlers.onBack}>
          <span aria-hidden="true">‹</span>
          <span>Notifications</span>
        </button>
        <Card>
          <EmptyState
            title="This is a legacy manual notification set"
            description="Personalized Notifications only shows automated weekly English and Sinhala notification sets."
          >
            <Action variant="primary" onClick={handlers.onBack}>Back to Notifications</Action>
          </EmptyState>
        </Card>
      </div>
    );
  }
  const steps = AUTOMATED_CAMPAIGN_STEPS;
  const stepProps = { model, derived, handlers };
  const instruction = getStepInstruction({ campaign, currentStep, dirty: model.dirty });
  const activeIndex = Math.max(0, steps.findIndex(step => step.id === activeTab));
  const activeStepMeta = steps[activeIndex];
  const selectTabFromKey = (event, index) => {
    let nextIndex;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = (index + 1) % steps.length;
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") nextIndex = (index + steps.length - 1) % steps.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = steps.length - 1;
    if (nextIndex === undefined) return;
    event.preventDefault();
    setActiveTab(steps[nextIndex].id);
    event.currentTarget.parentNode.children[nextIndex].focus();
  };
  const showDiscard = currentStep === CampaignStep.Content && (model.dirty || model.stale);
  const renderStep = stepId => {
    switch (stepId) {
      case CampaignStep.Audience:
        return (
          <AudienceStep
            campaign={campaign}
            catalogue={derived.catalogue}
            audience={model.audience}
            audienceList={model.selectedDay ? "six-day" : "lead-list"}
            selectedDay={model.selectedDay}
            isBusy={model.busy}
            onAudiencePage={handlers.onAudiencePage}
            onAudienceListChange={handlers.onAudienceListChange}
          />
        );
      case CampaignStep.Content:
        return <ContentStep {...stepProps} />;
      case CampaignStep.Test:
        return <TestStep {...stepProps} />;
      case CampaignStep.Analytics:
        return <AnalyticsStep campaign={campaign} counts={model.counts} logs={model.logs}
          outcomes={model.outcomes} windowDays={model.outcomeWindow} loading={model.outcomesLoading}
          error={model.outcomesError} onWindowChange={handlers.onOutcomeWindowChange} onRetry={handlers.onRetryOutcomes} />;
      default:
        return null;
    }
  };

  return (
    <div className="cs-page cs-page--details">
      <CampaignContextHeader
        campaign={campaign}
        stepNumber={activeIndex + 1}
        stepCount={steps.length}
        stepLabel={activeStepMeta.label}
        catalogue={derived.catalogue}
        cycleStart={findCycleStart(model.workspace, campaign.id)}
        isRefreshing={model.loading || model.busy}
        onBack={handlers.onBack}
        onRefresh={handlers.onRefresh}
      />

      {model.error && <Alert tone="danger" title="That action didn’t complete"><p className="cs-pre">{model.error}</p></Alert>}
      {model.readError && <Alert tone="danger" title="Couldn’t load the latest notification state"><p className="cs-pre">{model.readError}</p></Alert>}
      {model.notice && <Alert tone="success">{model.notice}</Alert>}

      {!model.forbidden && (
        <React.Fragment>
          <nav className="cs-detail-tabs" aria-label="Notification details">
            {steps.map((step, index) => (
              <button
                key={step.id}
                id={`cs-detail-tab-${step.id}`}
                type="button"
                role="tab"
                className={activeTab === step.id ? "is-active" : ""}
                aria-selected={activeTab === step.id}
                aria-controls={`cs-detail-panel-${step.id}`}
                tabIndex={activeTab === step.id ? 0 : -1}
                onClick={() => setActiveTab(step.id)}
                onKeyDown={event => selectTabFromKey(event, index)}
              >
                <span className="cs-detail-tabs__number">{index + 1}</span>
                <span>{step.label}</span>
              </button>
            ))}
          </nav>
          <section
            id={`cs-detail-panel-${activeStepMeta.id}`}
            role="tabpanel"
            className="cs-detail-tabpanel"
            aria-labelledby={`cs-detail-tab-${activeStepMeta.id}`}
            tabIndex={0}
          >
              <div className="cs-detail-tabpanel__intro">
                <h2 id={`cs-tab-${activeStepMeta.id}`}>{activeStepMeta.label}</h2>
                <p>{activeTab === currentStep ? instruction : activeStepMeta.id === CampaignStep.Analytics ? "Notification performance from saved delivery records" : `Review ${activeStepMeta.label.toLowerCase()}`}</p>
              </div>
              {renderStep(activeStepMeta.id)}
              {activeTab === currentStep && action && action.label !== "Next" && (
              <ActionBar
                action={action}
                isPending={model.busy}
                secondary={showDiscard && (
                  <Action disabled={model.busy} onClick={handlers.onDiscard}>
                    {model.stale ? "Discard draft and reload" : "Discard edits"}
                  </Action>
                )}
              />
              )}
              {activeTab === currentStep && !action && showDiscard && (
                <div className="cs-action-inline has-secondary">
                  <Action disabled={model.busy} onClick={handlers.onDiscard}>Discard draft and reload</Action>
                </div>
              )}
          </section>
        </React.Fragment>
      )}
    </div>
  );
};
